import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { slotsAPI } from '@hpanel/web-common/api/client'
import { ErrorBox, cardClass, inputClass, labelClass, toMessage } from '../../components/sites/siteUi'
import { useSlot } from '../../components/sites/useSlot'

/**
 * Add a subdomain served from its own folder (/client/sites/:containerId/subdomains/new).
 * The parent is the site's primary or one of its parked names; the page shows
 * the full name that will be created. No approval is needed. Its own page
 * (CLAUDE.md §15).
 */
export const AddSubdomainPage: React.FC = () => {
  const containerId = Number(useParams<{ containerId: string }>().containerId)
  const navigate = useNavigate()
  const { slot, loading, error: loadError } = useSlot(containerId)
  const [parentId, setParentId] = useState('')
  const [prefix, setPrefix] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const parents = slot ? [slot.primary, ...slot.parked] : []
  useEffect(() => {
    if (slot && !parentId) setParentId(String(slot.primary.id))
  }, [slot, parentId])

  const parent = parents.find((p) => String(p.id) === parentId)
  const clean = prefix.trim().toLowerCase().replace(/\.+$/, '')
  const fullName = parent && clean ? `${clean}.${parent.name}` : ''

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!parent || !fullName) return
    setSaving(true)
    setError(null)
    try {
      await slotsAPI.addSubdomain(containerId, { parent_domain_id: parent.id, name: fullName })
      navigate('/client/sites', { state: { flash: `Added ${fullName}` } })
    } catch (err: unknown) {
      setError(toMessage('add subdomain', err, 'Failed to add the subdomain'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div data-testid="site-subdomain-page" className="space-y-6 max-w-3xl">
      <div>
        <Link to="/client/sites" className="text-sm text-primary-600 hover:underline">
          ← Sites
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">Add a subdomain</h1>
        {slot && (
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            A subdomain of <strong className="font-mono">{slot.primary.name}</strong> (or one of its parked
            domains) serves its own folder.
          </p>
        )}
      </div>

      <ErrorBox message={error ?? loadError} testId="site-subdomain-error" />

      {loading ? (
        <p className="text-gray-600 dark:text-gray-400">Loading…</p>
      ) : slot && !slot.supports_subdomains ? (
        <p data-testid="site-subdomain-unsupported" className="text-gray-700 dark:text-gray-300">
          {slot.needs_recreate
            ? 'This site cannot serve subdomains yet: it needs to be rebuilt. Ask your hosting provider.'
            : "This site's image cannot serve subdomains from separate folders. Ask your hosting provider if you need them."}
        </p>
      ) : slot ? (
        <form onSubmit={handleSubmit} className={cardClass}>
          <div>
            <label htmlFor="site-subdomain-parent" className={labelClass}>Parent domain</label>
            <select
              id="site-subdomain-parent"
              data-testid="site-subdomain-parent"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              className={inputClass}
            >
              {parents.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="site-subdomain-prefix" className={labelClass}>Name</label>
            <input
              id="site-subdomain-prefix"
              data-testid="site-subdomain-prefix"
              type="text"
              required
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              placeholder="blog"
              className={inputClass}
            />
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Full name: <span data-testid="site-subdomain-preview" className="font-mono">{fullName}</span>
            </p>
          </div>
          <button
            type="submit"
            data-testid="site-subdomain-submit"
            disabled={saving || !fullName}
            className="btn-primary disabled:opacity-60"
          >
            {saving ? 'Adding…' : 'Add subdomain'}
          </button>
        </form>
      ) : null}
    </div>
  )
}
